/**
 * Session overlays for bank questions Leo or the author already repaired.
 *
 * Entering compose sets `?edit=1`, and the shell remounts the hub on that
 * search change. React state on the list is wiped. These patches live in
 * sessionStorage so Edit and the sheet still see the wording that was applied.
 */

import type { LibraryItem, LibraryItemType } from "@/lib/mock/library"
import {
  caseIssueIdsClearedByEdit,
  choiceSetIsBroken,
  libraryAiSpotted,
  libraryCaseTag,
  openQuestionSpotted,
  reconcileAiSpotted,
  settledWritingAiSpotted,
  type LibraryAiSpotted,
} from "@/lib/library-ai-spotted"
import { questionCatalogById } from "@/lib/mock/question-catalog"
import { questionTypeAcceptsChoices } from "@/lib/question-type"

/**
 * One saved edit, in the shape every surface reads it back in.
 *
 * Whatever the composer can change has to be on here. `type` and `status`
 * were the two it could not: saving a question as Draft, or switching it
 * from Multiple Choice to Fill in the blank, wrote a new stem and new
 * options into the overlay and left the old type and the old status on every
 * list that showed the question. Applying it is `applyLibraryBankRowOverlay`
 * for library rows and `withCatalogOverlay` (in `assessment-details`) for
 * paper rows, and both read this same set.
 */
export type LibraryBankRowOverlay = {
  stem?: string
  title?: string
  /** Storage enum (`lib/question-type`). Set when the author changed format. */
  type?: LibraryItemType
  options?: { text: string; isCorrect?: boolean }[]
  /** Accepted strings per blank, for fill-in questions. */
  fillBlankAnswers?: string[][]
  rationale?: string
  categoryIds?: string[]
  categoryUnassigned?: boolean
  bloomLevel?: string
  difficulty?: LibraryItem["difficulty"]
  status?: LibraryItem["status"]
  aiSpotted?: LibraryAiSpotted
  /**
   * Findings the author has answered, by id.
   *
   * **The bug this exists to kill.** Applying a fix used to record nothing.
   * The only trace was the new wording, and whether a finding counted as
   * resolved was re-inferred from that wording on every render by
   * `stemEditClearsFinding`, which asks whether the rewrite still shares at
   * least three words of more than four letters with the original. A good
   * rewrite changes the wording — that is the point of it — so it routinely
   * shares fewer than three, and the finding came straight back. The author
   * could fix the same stem five times and watch Leo flag it five times.
   *
   * Resolution is a fact about what the author did, not something to guess
   * at from the result. It is recorded here, and
   * `resolveLeoSpottedFindings` is the only way to write it.
   */
  resolvedIssueIds?: string[]
  /** What Leo changed. Shown from the Looks good info control. */
  fixedSummary?: string
}

const STORAGE_KEY = "library-bank-row-overlays-v1"

function readStoredOverlays(): Record<string, LibraryBankRowOverlay> {
  if (typeof window === "undefined") return {}
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, LibraryBankRowOverlay>
    return parsed && typeof parsed === "object" ? parsed : {}
  } catch {
    return {}
  }
}

function writeStoredOverlays(next: Record<string, LibraryBankRowOverlay>) {
  if (typeof window === "undefined") return
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Session storage may be unavailable in private mode.
  }
}

export function overlayForLibraryBankRow(
  id: string,
  questionId?: string,
): LibraryBankRowOverlay | undefined {
  const stored = readStoredOverlays()
  return stored[id] ?? (questionId ? stored[questionId] : undefined)
}

/**
 * Record that the author has answered these findings.
 *
 * The one way `resolvedIssueIds` is written, and it unions rather than
 * replaces: fixing the choices must not un-resolve the stem the author
 * fixed a minute ago. `patchLibraryBankRowOverlay` cannot do this job
 * because a plain object spread overwrites the array.
 *
 * Every id the question is known by has to be passed in `alsoIds`. The bank
 * row, the catalog entry and the paper roster use three different keys for
 * the same question (`ai-case-q3`, `q3`, `CASE-2026-003`), so recording
 * against one of them leaves the other two still flagging it — which is the
 * same class of bug in a different place. `leoSpottedOverlayKeys` builds
 * the list.
 */
export function resolveLeoSpottedFindings(
  rowId: string,
  issueIds: readonly string[],
  alsoIds: readonly string[] = [],
) {
  const ids = issueIds.map((id) => id.trim()).filter(Boolean)
  if (ids.length === 0) return
  const stored = readStoredOverlays()
  const next = { ...stored }
  for (const key of [rowId, ...alsoIds]) {
    if (!key) continue
    const current = next[key] ?? {}
    next[key] = {
      ...current,
      resolvedIssueIds: [...new Set([...(current.resolvedIssueIds ?? []), ...ids])],
    }
  }
  writeStoredOverlays(next)
}

/** Undo a resolution, for the card's Reopen. */
export function reopenLeoSpottedFindings(
  rowId: string,
  issueIds: readonly string[],
  alsoIds: readonly string[] = [],
) {
  const drop = new Set(issueIds)
  const stored = readStoredOverlays()
  const next = { ...stored }
  for (const key of [rowId, ...alsoIds]) {
    const current = next[key]
    if (!current?.resolvedIssueIds) continue
    next[key] = {
      ...current,
      resolvedIssueIds: current.resolvedIssueIds.filter((id) => !drop.has(id)),
    }
  }
  writeStoredOverlays(next)
}

/**
 * Every key one question is stored under.
 *
 * `ai-case-q3` (bank row), `q3` (catalog / paper roster) and
 * `CASE-2026-003` (public code) are all the same question, and a write that
 * only lands on one of them is a write the other surfaces will not see.
 */
export function leoSpottedOverlayKeys(
  id: string,
  questionId?: string,
): string[] {
  const catalog = questionCatalogById(id) ?? questionCatalogById(questionId)
  return [
    ...new Set(
      [
        id,
        questionId,
        catalog?.id,
        catalog?.questionId,
        catalog ? `ai-case-${catalog.id}` : undefined,
      ].filter((key): key is string => Boolean(key)),
    ),
  ]
}

export function patchLibraryBankRowOverlay(
  rowId: string,
  patch: LibraryBankRowOverlay,
  alsoIds: readonly string[] = [],
) {
  const stored = readStoredOverlays()
  const merged = { ...stored[rowId], ...patch }
  const next = { ...stored, [rowId]: merged }
  for (const id of alsoIds) {
    if (id && id !== rowId) next[id] = { ...next[id], ...merged }
  }
  writeStoredOverlays(next)
}

export function applyLibraryBankRowOverlay(item: LibraryItem): LibraryItem {
  const overlay = overlayForLibraryBankRow(item.id, item.questionId)
  if (!overlay) return item
  const parsedPbi =
    item.pbi == null || Number.isNaN(Number(item.pbi)) ? undefined : Number(item.pbi)
  const fromStats = libraryAiSpotted({
    needsAttention: overlay.aiSpotted === "needs-fixing",
    ...(parsedPbi !== undefined ? { pbi: parsedPbi } : {}),
  })
  const savedOptions = overlay.options?.length ? overlay.options : undefined
  // A saved placeholder set must not replace a real key already on the row.
  // A row with no real key keeps whatever the author just applied, including
  // placeholders, so a refresh does not invent a new choice list.
  const baseHasRealOptions = Boolean(
    item.options?.length && !choiceSetIsBroken(item.options),
  )
  const overlayOptions =
    savedOptions && choiceSetIsBroken(savedOptions) && baseHasRealOptions
      ? undefined
      : savedOptions
  const stored =
    overlay.aiSpotted !== undefined
      ? overlay.aiSpotted
      : overlayOptions
        ? settledWritingAiSpotted(fromStats, libraryCaseTag(item.id))
        : item.aiSpotted
  const options = overlayOptions ?? item.options
  const catalog = questionCatalogById(item.id) ?? questionCatalogById(item.questionId)
  const stem = overlay.stem ?? item.stem
  const categoryIds = overlay.categoryIds?.length ? overlay.categoryIds : item.categoryIds
  const categoryUnassigned = overlay.categoryIds?.length
    ? false
    : overlay.categoryUnassigned ?? item.categoryUnassigned
  const shared = openQuestionSpotted({
    id: item.id,
    stem,
    baseStem: catalog?.stem,
    choiceTexts: options?.map((option) => option.text),
    baseChoiceTexts: catalog?.options?.map((option) => option.text),
    resolvedIssueIds: overlay.resolvedIssueIds,
    categoryUnassigned,
    categoryIds,
    options,
    pbi: parsedPbi ?? null,
    timesUsed: item.usageCount,
  })
  const aiSpotted = shared?.tag ?? reconcileAiSpotted({
    id: item.id,
    aiSpotted: stored,
    options,
    resolvedIssueIds: caseIssueIdsClearedByEdit({
      id: item.id,
      stem,
      baseStem: catalog?.stem,
      choiceTexts: options?.map((option) => option.text),
      baseChoiceTexts: catalog?.options?.map((option) => option.text),
    }),
  })
  return {
    ...item,
    ...(overlay.stem ? { stem: overlay.stem } : {}),
    ...(overlay.title ? { title: overlay.title } : {}),
    ...(overlay.type ? { type: overlay.type } : {}),
    // Answer choices belong to a type that has them. Keeping a saved option
    // list on a question the author just turned into an essay is how a Type
    // column and an answer key come to disagree on the same row. Only a type
    // the overlay actually states drops them: a row with no type recorded is
    // an unknown, not a claim that the question has no choices.
    ...(overlay.type && !questionTypeAcceptsChoices(overlay.type)
      ? { options: undefined }
      : overlayOptions
        ? { options: overlayOptions }
        : {}),
    ...(overlay.fillBlankAnswers?.length
      ? { fillBlankAnswers: overlay.fillBlankAnswers }
      : {}),
    ...(typeof overlay.rationale === "string" ? { rationale: overlay.rationale } : {}),
    ...(overlay.categoryIds?.length
      ? { categoryIds: overlay.categoryIds, categoryUnassigned: false }
      : overlay.categoryUnassigned
        ? { categoryIds: [], categoryUnassigned: true }
        : {}),
    ...(overlay.bloomLevel ? { bloomLevel: overlay.bloomLevel } : {}),
    ...(overlay.difficulty ? { difficulty: overlay.difficulty } : {}),
    ...(overlay.status ? { status: overlay.status } : {}),
    aiSpotted,
    ...(overlay.fixedSummary
      ? { fixedSummary: overlay.fixedSummary }
      : aiSpotted === "looks-good" && overlay.aiSpotted === "looks-good"
        ? { fixedSummary: "Leo updated the stem and the answer choices." }
        : {}),
  }
}

export function libraryItemsWithBankOverlays(
  items: readonly LibraryItem[],
): LibraryItem[] {
  return items.map((item) => applyLibraryBankRowOverlay(item))
}

/** The spotted list the bank column shows for this saved row. */
export function spottedForLibraryItem(item: LibraryItem) {
  const catalog = questionCatalogById(item.id) ?? questionCatalogById(item.questionId)
  return openQuestionSpotted({
    id: item.id,
    stem: item.stem,
    baseStem: catalog?.stem,
    choiceTexts: item.options?.map((option) => option.text),
    baseChoiceTexts: catalog?.options?.map((option) => option.text),
    categoryUnassigned: item.categoryUnassigned,
    categoryIds: item.categoryIds,
    options: item.options,
    pbi: item.pbi ?? null,
    timesUsed: item.usageCount,
  })
}
